/* ═══════════════════════════════════════════════════════════
   MSC Press & Media — Interactions
   GSAP scroll reveals, copy-to-clipboard, collapsible toggles
   ═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var ROOT_SEL = '.msc-press';
  var root = document.querySelector(ROOT_SEL);
  if (!root) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── MODULE 1: GSAP SCROLL REVEAL ─────────────────────────────

  function initReveal() {
    var els = root.querySelectorAll('.msc-press__reveal');
    if (!els.length) return;

    if (reducedMotion || typeof gsap === 'undefined') {
      for (var i = 0; i < els.length; i++) {
        els[i].classList.add('msc-press__reveal--visible');
      }
      return;
    }

    // set initial state
    gsap.set(els, { opacity: 0, y: 24 });

    for (var j = 0; j < els.length; j++) {
      (function (el, index) {
        ScrollTrigger.create({
          trigger: el,
          start: 'top 88%',
          once: true,
          onEnter: function () {
            gsap.to(el, {
              opacity: 1,
              y: 0,
              duration: 0.6,
              delay: index * 0.06 > 0.3 ? 0 : index * 0.06,
              ease: 'power3.out'
            });
          }
        });
      })(els[j], j);
    }
  }

  // ── MODULE 2: COPY TO CLIPBOARD ──────────────────────────────

  function initCopy() {
    var btns = root.querySelectorAll('[data-copy-target]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', handleCopy);
    }
  }

  function handleCopy(e) {
    var btn = e.currentTarget;
    var targetId = btn.getAttribute('data-copy-target');
    var target = document.getElementById(targetId);
    if (!target) return;

    var text = target.innerText || target.textContent;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () {
        showCopied(btn);
      });
    } else {
      // fallback
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); showCopied(btn); } catch (err) { /* silent */ }
      document.body.removeChild(ta);
    }
  }

  function showCopied(btn) {
    var original = btn.innerHTML;
    btn.classList.add('msc-press__btn--copied');
    btn.setAttribute('aria-label', 'Copied to clipboard');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied';
    setTimeout(function () {
      btn.classList.remove('msc-press__btn--copied');
      btn.removeAttribute('aria-label');
      btn.innerHTML = original;
    }, 2000);
  }

  // ── MODULE 3: FACTS COUNTER ──────────────────────────────────

  function initFactsCounter() {
    var factsStrip = root.querySelector('[data-press-facts]');
    if (!factsStrip) return;
    var facts = factsStrip.querySelectorAll('.msc-press__fact');
    var counters = factsStrip.querySelectorAll('[data-count-to]');

    if (reducedMotion || typeof gsap === 'undefined') {
      for (var i = 0; i < counters.length; i++) {
        var target = parseInt(counters[i].getAttribute('data-count-to'), 10);
        var prefix = counters[i].getAttribute('data-count-prefix') || '';
        var suffix = counters[i].getAttribute('data-count-suffix') || '';
        counters[i].textContent = prefix + target + suffix;
      }
      return;
    }

    // hide facts initially
    gsap.set(facts, { opacity: 0, y: 20 });

    // single ScrollTrigger: fade-in + stagger + count-up together
    ScrollTrigger.create({
      trigger: factsStrip,
      start: 'top 80%',
      once: true,
      onEnter: function () {
        // staggered reveal of each fact card
        gsap.to(facts, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out'
        });

        // count-up numbers (start after first card fades in)
        for (var j = 0; j < counters.length; j++) {
          (function (el) {
            var target = parseInt(el.getAttribute('data-count-to'), 10);
            var prefix = el.getAttribute('data-count-prefix') || '';
            var suffix = el.getAttribute('data-count-suffix') || '';
            var obj = { val: 0 };
            gsap.to(obj, {
              val: target,
              duration: 1.8,
              delay: 0.2,
              ease: 'power2.out',
              onUpdate: function () {
                el.textContent = prefix + Math.round(obj.val) + suffix;
              }
            });
          })(counters[j]);
        }
      }
    });
  }

  // ── MODULE 4: FOUNDER PHOTO PIN ─────────────────────────

  function initFounderPin() {
    var founder = root.querySelector('.msc-press__founder');
    var photo = root.querySelector('.msc-press__founder-photo');
    if (!founder || !photo) return;

    // Skip on mobile (single-column layout) or reduced motion
    if (window.innerWidth <= 768 || reducedMotion) return;

    // Pin distance = grid height minus photo (stats strip inside bio
    // column makes content tall enough for natural pin duration)
    var pinDistance = founder.offsetHeight - photo.offsetHeight;
    if (pinDistance <= 0) return;

    // Gather slide images for scroll-driven crossfade
    var slides = photo.querySelectorAll('.msc-press__founder-slide');
    var slideCount = slides.length;
    var activeSlide = 0;

    ScrollTrigger.create({
      trigger: photo,
      start: 'top 120',
      end: '+=' + pinDistance,
      pin: true,
      pinSpacing: false,
      invalidateOnRefresh: true,
      onUpdate: slideCount > 1 ? function (self) {
        // transition thresholds — switch early so photos match the story
        // photo 1: 0–25%, photo 2: 25–55%, photo 3: 55–100%
        var p = self.progress;
        var zone;
        if (slideCount === 3) {
          zone = p < 0.08 ? 0 : p < 0.35 ? 1 : 2;
        } else {
          zone = p < 0.4 ? 0 : 1;
        }
        if (zone !== activeSlide) {
          gsap.to(slides[activeSlide], { opacity: 0, duration: 0.6, ease: 'power2.inOut' });
          gsap.to(slides[zone], { opacity: 1, duration: 0.6, ease: 'power2.inOut' });
          activeSlide = zone;
        }
      } : null
    });
  }

  // ── MODULE 5: PRESS RELEASE PIN + RED FILL ─────────────────

  function initReleasePin() {
    var header = root.querySelector('[data-release-pin]');
    if (!header) return;
    var line = header.querySelector('[data-release-line]');
    var title = header.querySelector('[data-release-title]');
    if (!line || !title) return;

    // Skip on mobile or reduced motion
    if (window.innerWidth <= 768 || reducedMotion) {
      gsap.set(line, { scaleX: 1 });
      return;
    }

    // Set up the title for gradient fill (left-to-right red wipe)
    title.style.backgroundImage = 'linear-gradient(90deg, #C1282D 50%, rgba(255,255,255,0.95) 50%)';
    title.style.backgroundSize = '200% 100%';
    title.style.backgroundPosition = '100% 0';
    title.style.webkitBackgroundClip = 'text';
    title.style.backgroundClip = 'text';
    title.style.webkitTextFillColor = 'transparent';
    title.style.color = 'transparent';

    // Scrub fill — no pin, just fills as you scroll past
    ScrollTrigger.create({
      trigger: header,
      start: 'top 70%',
      end: 'bottom 30%',
      scrub: true,
      onUpdate: function (self) {
        var p = self.progress;

        // Line draws first 15%
        var lineProgress = Math.min(p / 0.15, 1);
        gsap.set(line, { scaleX: lineProgress });

        // Headline fills red
        var fillRaw = p < 0.15 ? 0 : Math.min((p - 0.15) / 0.85, 1);
        var fillEased = fillRaw * fillRaw * (3 - 2 * fillRaw);
        var bgPos = 100 - (fillEased * 100);
        title.style.backgroundPosition = bgPos + '% 0';
      }
    });
  }

  // ── MODULE 6: COLLAPSIBLE TOGGLES ───────────────────────────

  function initCollapses() {
    var toggles = root.querySelectorAll('.msc-press__social-toggle');
    for (var i = 0; i < toggles.length; i++) {
      var btn = toggles[i];
      var group = btn.closest('.msc-press__social-group');
      var body = group ? group.querySelector('.msc-press__social-body') : null;
      var isOpen = group && group.classList.contains('msc-press__social-group--open');

      // set initial aria state
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (body) {
        var bodyId = body.id || ('press-social-' + i);
        body.id = bodyId;
        btn.setAttribute('aria-controls', bodyId);
      }

      btn.addEventListener('click', handleToggle);
    }
  }

  function handleToggle(e) {
    var btn = e.currentTarget;
    var group = btn.closest('.msc-press__social-group');
    if (!group) return;
    group.classList.toggle('msc-press__social-group--open');
    var isOpen = group.classList.contains('msc-press__social-group--open');
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  // ── MODULE 7: GALLERY MODALS (Photo + Logo) ────────────────

  function initModal(modalId, triggerAttr) {
    var modal = document.getElementById(modalId);
    if (!modal) return null;

    document.body.appendChild(modal);

    var closeBtn = modal.querySelector('.msc-press__gallery-modal-close');
    var triggers = root.querySelectorAll('[' + triggerAttr + ']');

    for (var i = 0; i < triggers.length; i++) {
      (function (m) {
        triggers[i].addEventListener('click', function (e) {
          e.preventDefault();
          m.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        });
      })(modal);
    }

    if (closeBtn) closeBtn.addEventListener('click', function () {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    });

    modal.addEventListener('click', function (e) {
      if (e.target === modal) {
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });

    return modal;
  }

  function initVideoModal() {
    var modal = document.getElementById('video-gallery-modal');
    if (!modal) return;

    document.body.appendChild(modal);

    var closeBtn = modal.querySelector('.msc-press__video-modal-close');
    var triggers = root.querySelectorAll('[data-open-video-gallery]');

    function pauseAllVideos() {
      var videos = modal.querySelectorAll('video');
      for (var i = 0; i < videos.length; i++) {
        videos[i].pause();
      }
    }

    function closeVideoModal() {
      pauseAllVideos();
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    for (var i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', function (e) {
        e.preventDefault();
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });
    }

    if (closeBtn) closeBtn.addEventListener('click', closeVideoModal);

    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeVideoModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
        closeVideoModal();
      }
    });
  }

  function initGalleryModals() {
    initModal('photo-gallery-modal', 'data-open-gallery');
    initModal('logo-gallery-modal', 'data-open-logo-gallery');
    initVideoModal();
  }

  // ── MODULE 8: PHOTO LIGHTBOX ────────────────────────────────
  var lightbox = null;
  var lightboxImg = null;
  var lightboxCaption = null;
  var lightboxDownload = null;
  var activeItems = [];
  var currentIndex = 0;

  function initLightbox() {
    lightbox = document.getElementById('photo-lightbox');
    if (!lightbox) return;

    // Move to body (same ScrollSmoother fix)
    document.body.appendChild(lightbox);

    lightboxImg = lightbox.querySelector('.msc-press__lightbox-img');
    lightboxCaption = lightbox.querySelector('.msc-press__lightbox-caption');
    lightboxDownload = lightbox.querySelector('.msc-press__lightbox-download');
    var closeBtn = lightbox.querySelector('.msc-press__lightbox-close');
    var prevBtn = lightbox.querySelector('.msc-press__lightbox-prev');
    var nextBtn = lightbox.querySelector('.msc-press__lightbox-next');

    // Collect items from all gallery modals
    var modalIds = ['photo-gallery-modal', 'logo-gallery-modal'];
    for (var m = 0; m < modalIds.length; m++) {
      var modal = document.getElementById(modalIds[m]);
      if (!modal) continue;
      var items = modal.querySelectorAll('.msc-press__gallery-item');
      for (var i = 0; i < items.length; i++) {
        (function (itemList, index) {
          itemList[index].addEventListener('click', function () {
            activeItems = itemList;
            openLightbox(index);
          });
        })(items, i);
      }
    }

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); showPrev(); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); showNext(); });

    // Click backdrop closes
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });

    document.addEventListener('keydown', function (e) {
      if (lightbox.getAttribute('aria-hidden') !== 'false') return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    });
  }

  function openLightbox(index) {
    if (!lightbox || !activeItems.length) return;
    currentIndex = index;
    updateLightbox();
    lightbox.setAttribute('aria-hidden', 'false');
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.setAttribute('aria-hidden', 'true');
  }

  function showPrev() {
    currentIndex = (currentIndex - 1 + activeItems.length) % activeItems.length;
    updateLightbox();
  }

  function showNext() {
    currentIndex = (currentIndex + 1) % activeItems.length;
    updateLightbox();
  }

  function updateLightbox() {
    var item = activeItems[currentIndex];
    if (!item) return;
    var img = item.querySelector('.msc-press__gallery-img');
    var caption = item.querySelector('.msc-press__gallery-caption');
    var credit = item.querySelector('.msc-press__gallery-credit');

    if (lightboxImg && img) {
      lightboxImg.src = img.src;
      lightboxImg.alt = img.alt;
    }
    if (lightboxDownload && img) {
      lightboxDownload.href = img.src;
    }
    if (lightboxCaption) {
      var html = '';
      if (caption) html += '<span>' + caption.textContent + '</span>';
      if (credit) html += '<span class="msc-press__lightbox-credit">' + credit.textContent + '</span>';
      lightboxCaption.innerHTML = html;
    }
  }

  // ── INIT ─────────────────────────────────────────────────────

  function init() {
    initReveal();
    initFactsCounter();
    initFounderPin();
    initReleasePin();
    initCopy();
    initCollapses();
    initGalleryModals();
    initLightbox();

    // Force pin recalculation after layout settles
    setTimeout(function () {
      ScrollTrigger.refresh(true);
    }, 800);
  }

  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    init();
  } else {
    // wait for GSAP
    var attempts = 0;
    var check = setInterval(function () {
      attempts++;
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        clearInterval(check);
        init();
      } else if (attempts > 50) {
        clearInterval(check);
        // run without GSAP
        var els = root.querySelectorAll('.msc-press__reveal');
        for (var i = 0; i < els.length; i++) {
          els[i].classList.add('msc-press__reveal--visible');
        }
        initCopy();
        initCollapses();
      }
    }, 100);
  }
})();
